import {Component, EventEmitter, Input, OnChanges, Output} from "@angular/core";
import {IOrganization} from "../../../interfaces/IOrganization";
import {ILocation} from "../../../interfaces/ILocation";

/** How many matched usernames are listed on a row before the rest are collapsed into a counter. */
const MAX_VISIBLE_USERNAMES = 3;

interface ILocationRow {
  location: ILocation;
  disabled: boolean;
  disabledReason: string;
  usernames: string[];
  extraUsernames: number;
}

@Component({
  selector: 'app-organization-with-locations-form',
  templateUrl: './organization-with-locations-form.component.html',
  styleUrls: ['./organization-with-locations-form.component.scss']
})
export class OrganizationWithLocationsFormComponent implements OnChanges {
  @Input() organization: IOrganization;
  @Output() _locationPressed = new EventEmitter<ILocation>();
  @Input() showCheckboxes: boolean;
  @Input() disabledLocationIds: string[] = [];
  @Input() requireActive = false;
  /** Set while a search is running so each row can show which usernames matched it. */
  @Input() searchTerm = "";

  rows: ILocationRow[] = [];

  ngOnChanges(): void {
    this.rows = (this.organization?.locations ?? []).map(location => this.buildRow(location));
  }

  get selectableCount(): number {
    return this.rows.filter(row => !row.disabled).length;
  }

  get selectedCount(): number {
    return this.rows.filter(row => row.location.isSelected).length;
  }

  get allSelected(): boolean {
    return this.selectableCount > 0 &&
      this.rows.every(row => row.disabled || row.location.isSelected);
  }

  get someSelected(): boolean {
    return !this.allSelected &&
      this.rows.some(row => !row.disabled && row.location.isSelected);
  }

  toggleLocation(row: ILocationRow, checked: boolean): void {
    if (row.disabled) return;
    row.location.isSelected = checked;
    this._locationPressed.emit(row.location);
  }

  toggleAll(checked: boolean): void {
    this.rows.forEach(row => {
      if (row.disabled || !!row.location.isSelected === checked) return;
      this.toggleLocation(row, checked);
    });
  }

  trackByLocationId(_index: number, row: ILocationRow): string {
    return row.location.id;
  }

  isDisabled(location: ILocation): boolean {
    return this.disabledLocationIds.includes(location.id) ||
      (this.requireActive && location.isActive === false);
  }

  disabledReason(location: ILocation): string {
    if (this.disabledLocationIds.includes(location.id)) return "source location";
    if (this.requireActive && location.isActive === false) return "inactive";
    return "";
  }

  private buildRow(location: ILocation): ILocationRow {
    // Usernames are only meaningful while searching: the API narrows them to the search hits then,
    // and otherwise returns every user of the location.
    const matched = this.searchTerm ? (location.foundUsernames ?? []) : [];
    return {
      location,
      disabled: this.isDisabled(location),
      disabledReason: this.disabledReason(location),
      usernames: matched.slice(0, MAX_VISIBLE_USERNAMES),
      extraUsernames: Math.max(0, matched.length - MAX_VISIBLE_USERNAMES),
    };
  }
}
